import React from 'react';
import {
  Card,
  Text,
  Button,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  CheckmarkCircle24Filled,
  DismissCircle24Filled,
  Warning24Filled,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalL,
  },
  card: {
    padding: tokens.spacingVerticalL,
    width: '100%',
    maxWidth: '400px',
  },
  iconSuccess: {
    color: tokens.colorPaletteGreenForeground1,
    fontSize: '48px',
  },
  iconError: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: '48px',
  },
  iconWarning: {
    color: tokens.colorPaletteYellowForeground1,
    fontSize: '48px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalM,
    textAlign: 'center',
  },
  title: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
  },
  message: {
    color: tokens.colorNeutralForeground2,
  },
  details: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    width: '100%',
    wordBreak: 'break-word',
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
  },
});

export type ResultType = 'success' | 'error' | 'warning';

export interface ResultMessageProps {
  type: ResultType;
  title: string;
  message: string;
  details?: string;
  onClose?: () => void;
  onRetry?: () => void;
}

export const ResultMessage: React.FC<ResultMessageProps> = ({
  type,
  title,
  message,
  details,
  onClose,
  onRetry,
}) => {
  const styles = useStyles();

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckmarkCircle24Filled className={styles.iconSuccess} />;
      case 'error':
        return <DismissCircle24Filled className={styles.iconError} />;
      case 'warning':
        return <Warning24Filled className={styles.iconWarning} />;
    }
  };

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <div className={styles.content}>
          {getIcon()}
          <Text className={styles.title}>{title}</Text>
          <Text className={styles.message}>{message}</Text>
          {details && <Text className={styles.details}>{details}</Text>}
          <div className={styles.actions}>
            {onRetry && type === 'error' && (
              <Button appearance="secondary" onClick={onRetry}>
                Try Again
              </Button>
            )}
            {onClose && (
              <Button appearance="primary" onClick={onClose}>
                Close
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};
